import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { CheckoutValidationResponse } from '../../types';
import { DateUtils } from '../../utils';

interface CheckoutConfirmationDialogProps {
  open: boolean;
  onConfirm: (proposedDueDate?: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  validationData: CheckoutValidationResponse | null;
  orderNumber: string;
}

const CheckoutConfirmationDialog: React.FC<CheckoutConfirmationDialogProps> = ({
  open,
  onConfirm,
  onCancel,
  isSubmitting,
  validationData,
  orderNumber,
}) => {
  const [proposedDueDate, setProposedDueDate] = useState<string>('');

  const handleConfirm = () => {
    onConfirm(proposedDueDate || undefined);
  };

  const handleClose = () => {
    setProposedDueDate('');
    onCancel();
  };

  if (!validationData) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Checkout Confirmation</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to check out order <strong>{orderNumber}</strong>. Once checked out, the
          order will be released into production and can no longer be modified.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Order Summary
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Order Total:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              ${validationData.orderTotalPrice.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total Lineal Feet:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {validationData.orderTotalFeet.toFixed(2)} ft
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Due Date Calculation
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Lead Time:
            </Typography>
            <Typography variant="body2">
              {validationData.leadTimeDays} business day
              {validationData.leadTimeDays !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Max Production Per Day:
            </Typography>
            <Typography variant="body2">
              {validationData.maxLinealFeetPerDay} lineal feet
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Calculated Due Date:
            </Typography>
            <Typography variant="body2" fontWeight={600} color="primary">
              {validationData.calculatedDueDate
                ? DateUtils.toDisplayDate(validationData.calculatedDueDate)
                : 'N/A'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Proposed Due Date (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Override the calculated due date with a proposed date if needed.
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={proposedDueDate}
            onChange={e => setProposedDueDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          This action cannot be undone by Dealers or CSRs. Only Administrators and Supervisors can
          return an order to Draft status.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Processing...' : 'Confirm Checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckoutConfirmationDialog;
